<template>
  <div
    v-show="avoidHidingIfNotTranslated || !shouldHideContent"
    class="flex flex-row cursor-pointer relative"
    :class="{
      'gap-2 p-4': !newLayout,
      // ANNOUNCEMENTS modal: the design's card has 32px padding and a 24px
      // gap between the avatar rail and the content column. On a narrow mobile
      // viewport that 32px padding squeezes the header row (the username truncates
      // hard and the timestamp wraps), so the new layout steps the padding and
      // rail gap down on mobile and only reaches the full 32px/24px at the sm
      // breakpoint where the surface is wide enough.
      'gap-4 p-5 sm:gap-6 sm:p-8': newLayout,
      'rounded-3xl': !channelStore.isAsMonacoFanToken && !newLayout,
      // ANNOUNCEMENTS modal: the thread cards sit on the white modal
      // surface, so they take the neutral light-grey `surface-secondary` fill
      // (matching the design's announcement cards) with the 32px radius. The
      // legacy channel-page treatment keeps the white `bg-surface` fill and the
      // 24px `rounded-3xl` byte-for-byte.
      'bg-surface-secondary rounded-[2rem]': newLayout,
      'bg-surface': !newLayout,
      // The brand VIP card treatment (pink fill + red outline) is authored for
      // the dark legacy channel page. In the light new-layout modal it makes
      // every announcement card read as a garish red block, so it is suppressed
      // there; the card stays neutral exactly like the design.
      '!bg-fill-brand-secondary outline outline-2 outline-offset-[-2px] outline-border-brand':
        hasPaidMembership && !newLayout,
    }"
    @click="emit('cardClick', thread.id)"
  >
    <div class="flex flex-col items-center">
      <Avatar
        :user="localThread.created_by ?? undefined"
        :vip-border="hasPaidMembership && !newLayout"
      />

      <div
        class="flex-1 w-[1px] mt-1 mb-8 rounded-full from-fill-brand-secondary to-transparent bg-gradient-to-b"
      />
    </div>
    <div class="flex flex-col flex-grow" :class="newLayout ? 'gap-2' : 'gap-1'">
      <div
        class="flex justify-between items-start"
        :class="{ 'gap-2': newLayout }"
      >
        <div class="flex gap-2 items-center" :class="{ 'min-w-0': newLayout }">
          <UsernameWithBadge
            :username="
              localThread.created_by ? localThread.created_by.username : ''
            "
            :is-vip="hasPaidMembership"
            :on-light-surface="newLayout"
            text-class="uppercase !text-base !font-serif !font-normal"
          />
          <CountryBadge :icon="thread.created_by?.country_iso" />

          <span
            v-if="isPinned"
            class="pointer-events-none aspect-square p-1.5 rounded-xl bg-black bg-opacity-15 backdrop-blur-lg"
          >
            <Icon
              icon="ix:pin-filled"
              :class="[
                newLayout ? 'text-icon-brand' : 'fill-basics-primary',
                'size-5',
              ]"
            />
          </span>
        </div>

        <div
          class="flex items-center justify-center gap-3"
          :class="{ 'shrink-0': newLayout }"
        >
          <span
            class="text-text-secondary text-sm"
            :class="{ 'whitespace-nowrap': newLayout }"
          >
            <span v-once>{{
              timeAgo(new Date(localThread.created_at), userStore.userLocale)
            }}</span>
          </span>

          <ThreadItemDropdown
            :is-thread="isThread"
            :is-pinned="isPinned"
            :can-delete="canDelete"
            :can-edit="canEdit"
            :can-pin="channelStore.permissions.manageThreads"
            @report="showReportConfirmation = true"
            @edit="onEditClick"
            @delete="showDeleteConfirmation = true"
            @toggle-pin="onPinToggleClick"
          />
        </div>
      </div>
      <!-- New-layout announcement body is 14px (text-sm); legacy is 16px. -->
      <p
        class="text-text whitespace-pre-line"
        :class="newLayout ? 'text-sm' : 'text-base'"
      >
        {{ threadContent }}
      </p>

      <TranslateButton
        v-if="canShowTranslateButton"
        v-model:auto-translate="localAutoTranslate"
        class="self-start"
        :is-translated="isTranslated"
        :has-translation="hasTranslation"
      />

      <figure v-if="threadMedia.state !== 'empty'" class="w-64">
        <img
          v-if="threadMedia.state === 'image'"
          :src="threadMedia.contentUrl!"
          :alt="thread?.content"
          class="rounded-xl"
        />
        <video
          v-else-if="threadMedia.state === 'video'"
          :src="threadMedia.contentUrl!"
          class="rounded-xl"
          playsinline
          controls
        ></video>
      </figure>

      <!--
        Action row. ANNOUNCEMENTS modal: 24px (size-6)
        red icons, a 16px (gap-4) gap between like/comment, and 12px (text-xs)
        black counts. The legacy channel-page treatment keeps the larger 32px
        icons + 12px (gap-3) row byte-for-byte.
      -->
      <div
        class="flex items-center text-text"
        :class="newLayout ? 'gap-4' : 'gap-3'"
      >
        <button
          class="flex flex-col gap-1 items-center justify-center select-none"
          @click.stop.prevent="onLikeClick"
        >
          <Transition mode="out-in" name="bounce">
            <Icon
              v-if="localThread.has_liked"
              icon="ph:heart-fill"
              class="text-fill-brand"
              :class="newLayout ? 'size-6' : 'size-8'"
            />
            <Icon
              v-else
              icon="ph:heart"
              class="text-fill-brand"
              :class="newLayout ? 'size-6' : 'size-8'"
            />
          </Transition>

          <span :class="{ 'text-xs': newLayout }">{{
            localThread.num_of_likes
          }}</span>
        </button>

        <button
          class="flex flex-col gap-1 items-center justify-center"
          @click.stop.prevent="onReplyClick"
        >
          <Icon
            icon="basil:comment-outline"
            class="text-fill-brand"
            :class="newLayout ? 'size-6' : 'size-8'"
          />
          <span :class="{ 'text-xs': newLayout }">{{
            localThread.num_of_replies
          }}</span>
        </button>

        <button
          class="ml-auto flex flex-col items-center justify-between gap-2.5 border-none"
          @click.stop="onShareClick"
        >
          <Icon
            icon="mdi:ios-share"
            class="text-fill-brand"
            :class="newLayout ? 'size-6' : 'size-26u'"
          />
          <span class="text-xs">{{ $t("channel.thread.share") }}</span>
        </button>
      </div>
    </div>

    <Spinner v-if="pinStatusLoading" />
  </div>

  <DangerConfirmationModal
    v-model="showReportConfirmation"
    :title="$t('channel.thread.reportConfirmationTitle')"
    :confirm-label="$t('channel.thread.reportConfirmationLabel')"
    @confirm="onReportClick"
  />
  <DangerConfirmationModal
    v-model="showDeleteConfirmation"
    :title="
      isThread
        ? $t('channel.thread.deleteConfirmationThisThread')
        : $t('channel.thread.deleteConfirmationThisReply')
    "
    :confirm-label="$t('channel.thread.deleteConfirmButton')"
    @confirm="deleteThreadClick"
  >
  </DangerConfirmationModal>
</template>

<script setup lang="ts">
import {
  DeleteReplyDocument,
  LikeThreadDocument,
  ReportReplyDocument,
  ThreadType,
  UnlikeThreadDocument,
  PinThreadToChannelDocument,
  UnpinThreadFromChannelDocument,
} from "@/generated/graphql";

import { Icon } from "@iconify/vue";

import Avatar from "@/components/me/avatar.vue";
import { useAwaitAction } from "@/composables/use-await-action";
import { useMutation } from "@vue/apollo-composable";
import { computed, defineAsyncComponent, ref } from "vue";
import { useRouter } from "vue-router";
import { RouteNames } from "@/router/app.router";
import { useChannelStore } from "@/stores/channel.store";
import { timeAgo } from "@/utils/date";
import { useGatingForAction } from "@/features/gating/evaluation/use-gating-for-action";
import { ChannelPermission } from "@f3/shared/permissions";
import {
  type FragmentType,
  getFragmentData,
  graphql as gql,
} from "@/generated";
import { ExperienceAction, ExperienceType } from "@/enums/experience-type.enum";
import {
  EventCategory,
  TrackEventEnum,
} from "@/services/tracking/tracking-types";
import { useTrackingStore } from "@/stores/tracking.store";
import { useShareReferral } from "@/composables/use-share-referral";
import CountryBadge from "@/components/badge/country-badge.vue";
import {
  useLocalAutoTranslate,
  useObjectTranslation,
} from "@/stores/translation.store";
import TranslateButton from "../translate-button.vue";
import { useUserStore } from "@/stores/user.store";
import {
  useProvideGatingPermissions,
  type AuthorizationRuleGroupForGatingType,
} from "@/features/gating/use-gating-permissions";
import UsernameWithBadge from "@/components/me/username-with-badge.vue";
import { useI18n } from "vue-i18n";
import Spinner from "@/components/ui/spinner.vue";
import ThreadItemDropdown from "./thread-item-dropdown.vue";
import { useFeatureFlags } from "@/composables/use-feature-flags";
// Lazy load
const DangerConfirmationModal = defineAsyncComponent(
  () => import("@/components/show/modals/danger-confirmation-modal.vue"),
);

const ThreadlikeForCardFragment = gql(/* GraphQL */ `
  fragment ThreadlikeForCard on Threadlike {
    id
    content
    num_of_likes
    num_of_replies
    created_at
    created_by {
      id
      username
      avatar
      country_iso
      user_channel_memberships {
        channel_membership {
          permission_mask
        }
      }
    }
    thread_translations {
      content
      language
    }
    thread_communities {
      community {
        id
      }
    }
    has_liked
    media_content_url
    type
  }
`);

const props = defineProps<{
  threadFragment: FragmentType<typeof ThreadlikeForCardFragment>;
  authorizationRuleGroupFragment: AuthorizationRuleGroupForGatingType;
  avoidHidingIfNotTranslated?: boolean;
  isPinned?: boolean;
}>();

const { t } = useI18n();
const userStore = useUserStore();
const showReportConfirmation = ref(false);
const { getBoolean } = useFeatureFlags();
const newLayout = getBoolean("enable_new_channel_layout");

useProvideGatingPermissions(
  computed(() => props.authorizationRuleGroupFragment),
);

const thread = computed(() =>
  getFragmentData(ThreadlikeForCardFragment, props.threadFragment),
);
const isThread = computed(() => thread.value.type === ThreadType.Thread);
const canDelete = computed(
  () =>
    channelStore.permissions.manageThreads ||
    thread.value.created_by?.id === userStore.user?.id,
);
// Users with manageThreads can edit, but unless they have manageChannel, they can only edit their own threads
const canEdit = computed(
  () =>
    channelStore.permissions.manageChannel ||
    (channelStore.permissions.manageThreads &&
      thread.value.created_by?.id === userStore.user?.id),
);

const emit = defineEmits<{
  cardClick: [id: string];
  refetch: [];
}>();

const localThread = ref({ ...thread.value });
const channelStore = useChannelStore();
const trackingService = useTrackingStore();

const router = useRouter();

const localAutoTranslate = useLocalAutoTranslate();
const {
  text: threadContent,
  isTranslated,
  shouldHideContent,
  canShowTranslateButton,
  hasTranslation,
} = useObjectTranslation(
  computed(() => ({
    authorUserId: thread.value.created_by?.id,
    originalText: thread.value.content,
    objectAccessors: { objectId: thread.value.id, objectProperty: "content" },
    translatedText: thread.value.thread_translations[0]?.content,
    localAutoTranslate: localAutoTranslate.value,
  })),
);

const { mutate: likeThread } = useMutation(LikeThreadDocument);
const { mutate: unlikeThread } = useMutation(UnlikeThreadDocument);
const { mutate: report } = useMutation(ReportReplyDocument);
const { mutate: deleteReply } = useMutation(DeleteReplyDocument);
const { mutate: pinThread } = useMutation(PinThreadToChannelDocument);
const { mutate: unpinThread } = useMutation(UnpinThreadFromChannelDocument);

const showDeleteConfirmation = ref(false);

const { handler: onPinToggleClick, loading: pinStatusLoading } = useAwaitAction(
  async () => {
    if (props.isPinned) {
      const result = await unpinThread({ thread_id: thread.value.id });
      if (result?.data?.unpinThreadFromChannel) {
        channelStore.removePinnedThreadId(thread.value.id);
        emit("refetch");
      }
    } else {
      const result = await pinThread({ thread_id: thread.value.id });
      if (result?.data?.pinThreadToChannel) {
        channelStore.addPinnedThreadId(thread.value.id);
        emit("refetch");
      }
    }
  },
);

const awaitableLikeAction = useAwaitAction(async () => {
  if (localThread.value.has_liked) {
    unlikeThread({
      thread_id: thread.value.id,
    });
  } else {
    likeThread({
      thread_id: thread.value.id,
    });
  }
  trackingService.track({
    event: TrackEventEnum.ThreadLiked,
    category: EventCategory.Threads,
    metadata: {
      likeOrUnlike: thread.value.has_liked ? "unlike" : "like",
      experienceId: thread.value.id,
      experienceType: ExperienceType.Thread,
      experienceAction: ExperienceAction.LIKE_THREAD,
      // experienceMembership: channelStore.channelMemberships.find(
      //   (cm) => cm.authorization_rule_id === props.authorizationRuleId,
      // )?.name,
      experienceCreatorId: thread.value.created_by?.id,
      experienceCreatorUsername: thread.value.created_by?.username,
    },
  });

  localThread.value.has_liked = !localThread.value.has_liked;
  localThread.value.num_of_likes += localThread.value.has_liked ? 1 : -1;
});

const { handler: onLikeClick } = useGatingForAction(
  ChannelPermission.interactInThreads,
  awaitableLikeAction.handler,
  {
    gatingExperience: {
      experienceId: thread.value.id,
      experienceTitle: thread.value.content,
      experienceType: ExperienceType.Thread,
      experienceAction: ExperienceAction.LIKE_THREAD,
      experienceCreatorId: thread.value.created_by?.id,
      experienceCreatorUsername: thread.value.created_by?.username,
    },
  },
);

const onReplyClick = () => {
  router.push({
    name: RouteNames.ThreadsItem,
    params: {
      thread_id: thread.value.id,
      channel: channelStore.channelSlug,
    },
  });
};

const onEditClick = () => {
  router.push({
    name: RouteNames.ThreadsEdit,
    params: {
      threadId: thread.value.id,
      channel: channelStore.channelSlug,
    },
  });
};

const onReportClick = async () => {
  showReportConfirmation.value = false;
  await report({
    reply_id: thread.value.id,
  });
};

const deleteThreadClick = async () => {
  showDeleteConfirmation.value = false;

  await deleteReply({
    reply_id: thread.value.id,
  });
  emit("refetch");
};

const onShareClick = useShareReferral({
  openGraphObject: computed(() => ({
    title: t("channel.thread.report.ogShareTitle"),
  })),
  referralObject: computed(() => ({
    title: t("channel.thread.report.ogShareTitle"),
    payload: {
      experience_id: thread.value.id,
      experience_type: ExperienceType.Thread,
      community_id:
        thread.value.thread_communities?.length === 1
          ? thread.value.thread_communities.at(0)?.community.id
          : undefined,
    },
  })),
});

function getFileType(url: string) {
  const imageExtensions = [".png", ".jpg", ".jpeg", ".gif", ".webp"];
  const videoExtensions = [".mp4", ".m4v", ".mov", ".avi", ".mkv", ".webm"];

  const extension = url.split(".").pop()?.toLowerCase();

  if (imageExtensions.includes(`.${extension}`)) {
    return "image";
  } else if (videoExtensions.includes(`.${extension}`)) {
    return "video";
  } else {
    return "empty";
  }
}

const threadMedia = computed(() => {
  return {
    state: getFileType(localThread.value.media_content_url || ""),
    contentUrl: localThread.value.media_content_url,
  };
});

const hasPaidMembership = computed(() =>
  thread.value?.created_by?.user_channel_memberships?.some(
    (cm) => cm.channel_membership?.permission_mask > 0,
  ),
);
</script>

<style>
.bounce-enter-active {
  animation: bounce-in 0.25s;
}

.bounce-leave-active {
  animation: bounce-in 0.25s reverse;
}

@keyframes bounce-in {
  0% {
    transform: scale(0);
  }

  50% {
    transform: scale(1.25);
  }

  100% {
    transform: scale(1);
  }
}
</style>
